import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import api from '../api/axios';

const SettingsContext = createContext();

const getDefaultSettings = () => ({
    general: {
        siteName: 'Wise International Consultancy',
        tagline: 'Your Gateway to Success',
        email: 'info@example.com',
        phone: '+977-9800000000',
        whatsapp: '+9779800000000',
        address: { line1: '', city: 'Kathmandu', country: 'Nepal' }
    },
    socialMedia: {},
    homePage: {
        hero: {},
        stats: { isVisible: true, items: [] },
        about: { isVisible: true },
        services: { isVisible: true, items: [] },
        countries: { isVisible: true, items: [] },
        faq: { isVisible: true, items: [] },
        cta: { isVisible: true }
    },
    header: { menuItems: [] },
    footer: { quickLinks: [], studyDestinations: [] },
    whatsappButton: { isVisible: true }
});

export const SettingsProvider = ({ children }) => {
    // Check if we have cached data from a previous visit
    const hasCacheRef = useRef(!!sessionStorage.getItem('siteSettings'));

    const [settings, setSettings] = useState(() => {
        const cached = sessionStorage.getItem('siteSettings');
        if (cached) {
            try { return JSON.parse(cached); } catch { /* ignore */ }
        }
        return getDefaultSettings();
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ✅ NEW: If cache exists → ready immediately. If not → wait for API.
    const [isReady, setIsReady] = useState(hasCacheRef.current);

    const hasFetched = useRef(false);
    const retryCount = useRef(0);
    const maxRetries = 3;

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/settings');
            setSettings(response.data.data);
            retryCount.current = 0;
        } catch (err) {
            console.error('Failed to fetch settings:', err);
            if (err.response?.status === 429) {
                if (retryCount.current < maxRetries) {
                    retryCount.current += 1;
                    const delay = Math.pow(2, retryCount.current) * 2000;
                    setTimeout(() => fetchSettings(), delay);
                    return;
                }
            }
            setError('Failed to load settings');
        } finally {
            setLoading(false);
            // ✅ Always unblock after first fetch attempt (success or fail)
            setIsReady(true);
        }
    }, []);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        fetchSettings();
    }, [fetchSettings]);

    useEffect(() => {
        if (settings && !error) {
            sessionStorage.setItem('siteSettings', JSON.stringify(settings));
        }
    }, [settings, error]);

    const updateSettings = async (section, data) => {
        try {
            const response = await api.put(`/settings/${section}`, data);
            await fetchSettings();
            return response.data;
        } catch (err) {
            console.error('Failed to update settings:', err);
            throw err;
        }
    };

    // ✅ FIX: On FIRST visit (no cache), show a loading screen until API responds.
    //    On REPEAT visits, cache is available so isReady=true from the start (no flash).
    if (!isReady) {
        return (
            <SettingsContext.Provider value={{ 
                settings, loading: true, error: null, fetchSettings, updateSettings 
            }}>
                <div 
                    className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center" 
                    role="status"
                >
                    <div className="text-center">
                        {/* Logo — uses the same file from your public folder */}
                        <img 
                            src="/wiselogo.png" 
                            alt="" 
                            className="h-16 w-auto mx-auto mb-6 opacity-90"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="relative mx-auto w-10 h-10 mb-4">
                            <div className="w-10 h-10 border-[3px] border-slate-700 rounded-full"></div>
                            <div className="w-10 h-10 border-[3px] border-transparent border-t-primary-500 rounded-full animate-spin absolute top-0 left-0"></div>
                        </div>
                        <span className="sr-only">Loading site…</span>
                    </div>
                </div>
            </SettingsContext.Provider>
        );
    }

    return (
        <SettingsContext.Provider value={{
            settings,
            loading,
            error,
            fetchSettings,
            updateSettings
        }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) throw new Error('useSettings must be used within a SettingsProvider');
    return context;
};

export default SettingsContext;